import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { Background } from '../../shared/components/background/background';
import { Connect } from '../../shared/components/connect/connect';
import { HeroSection } from '../../shared/components/hero-section/hero-section';
import { BlogCard } from '../../shared/components/blog-card/blog-card';
import { BlogContentService } from '../../shared/services/blog-content.service';
import { IBlogPostMeta } from '../../shared/interfaces/blog-post.interface';
import { SeoService } from '../../shared/services/seo.service';

const PAGE_SIZE = 24;

@Component({
  selector: 'app-blog',
  imports: [CommonModule, Background, HeroSection, Connect, BlogCard],
  templateUrl: './blog.html',
  styleUrl: './blog.scss',
})
export class Blog {
  protected file = 'blog';

  private blogContent = inject(BlogContentService);
  private seo = inject(SeoService);

  constructor() {
    this.seo.apply({
      title: 'Blog',
      description:
        'Insights, guides, and field reports from SEM Nexus on app development, ASO, marketing, AI, and growth.',
      canonical: '/blog',
      type: 'website',
    });
    this.seo.setBreadcrumbs([
      { name: 'Home', url: '/' },
      { name: 'Blog' },
    ]);
  }

  protected allPosts = toSignal(this.blogContent.getPosts(), { initialValue: [] as IBlogPostMeta[] });
  protected selectedCategory = signal<string | null>(null);
  protected page = signal(1);

  protected categories = computed(() => {
    const seen = new Map<string, string>();
    for (const p of this.allPosts()) {
      p.categories.forEach((slug, i) => {
        if (!seen.has(slug)) seen.set(slug, p.category_names[i] ?? slug);
      });
    }
    return [...seen.entries()]
      .map(([slug, name]) => ({ slug, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  protected filtered = computed(() => {
    const cat = this.selectedCategory();
    if (!cat) return this.allPosts();
    return this.allPosts().filter((p) => p.categories.includes(cat));
  });

  protected visible = computed(() => this.filtered().slice(0, this.page() * PAGE_SIZE));

  protected hasMore = computed(() => this.visible().length < this.filtered().length);

  protected selectCategory(slug: string | null): void {
    this.selectedCategory.set(slug);
    this.page.set(1);
  }

  protected loadMore(): void {
    this.page.update((p) => p + 1);
  }
}
